import type { ReactNode } from 'react';
import { formatCurrency, formatDate } from '~/utils';
import { Flex, Heading, Tag, Divider, Card, Text, Popover } from '@applyboard/crystal-ui';
import { ChevronDownOutlineIcon } from '~/icons';

import { useAppProcessing } from '~/pages/ApplicationDetails/context';

function AttributesView(props: { items: Array<{ label: string; value: ReactNode }>; line: string }) {
  return (
    <Flex gap={1} align="end" direction="row" id={`program-line-${props.line}`}>
      {props.items.map((item, idx) => (
        <Flex key={item.label} gap={3} direction="row" pr={2}>
          <Flex direction="row" gap={1} aria-describedby={item.label}>
            <Text id={item.label} variant="labelM">
              {item.label}
            </Text>
            {typeof item.value === 'string' ? (
              <Text variant="bodyM" aria-labelledby={item.label}>
                {item.value}
              </Text>
            ) : (
              item.value
            )}
          </Flex>
          {idx !== props.items.length - 1 && <Divider direction="vertical" />}
        </Flex>
      ))}
    </Flex>
  );
}

type ProgramDetailsProps = { lineSplit: boolean };
export function ProgramDetails(props: ProgramDetailsProps) {
  const { application } = useAppProcessing();
  const { program, campus, intakeTerm } = application.programIntake;
  const { isManualPayment, amountReceived, description, dateReceived } = application.applicationFee;
  const amount = application.applicationFee.payableFee ?? application.applicationFee.amount;
  const isFree = amount === 0;
  const isNotPaid = !amountReceived || amountReceived === 0;
  const displayAmount = !isNotPaid ? amountReceived ?? amount : 0;
  const feeStatusValue = isFree ? 'Free' : isNotPaid ? 'Not Paid' : isManualPayment ? 'Paid (Manually)' : 'Paid';
  const manualDateValue = dateReceived ? formatDate(new Date(dateReceived)) : '-';
  const items = [
    { label: 'Program Code', value: program.code },
    { label: 'Campus', value: campus.name },
    { label: 'Application ID', value: application.humanReadableId },
    { label: 'Date Submitted', value: formatDate(new Date(application.submittedAt)) },
    {
      label: 'Fee Status',
      value:
        isManualPayment && !isNotPaid ? (
          <Flex direction="row" align="center" gap={1} aria-labelledby="Fee Status">
            <Text variant="bodyM">{feeStatusValue}</Text>
            <Popover>
              <Popover.Trigger>
                <button
                  type="button"
                  aria-label="Show manual payment details"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronDownOutlineIcon />
                </button>
              </Popover.Trigger>
              <Popover.Content width="md" noShortDescription={true}>
                <Popover.Heading>Manual payment details</Popover.Heading>
                <div style={{ marginTop: -24 }}>
                  <Flex direction="column" gap={4}>
                    {description ? (
                      <Flex direction="column" gap={1}>
                        <Text variant="labelM" intent="secondary">
                          Description
                        </Text>
                        <Text variant="bodyM">{description}</Text>
                      </Flex>
                    ) : null}
                    <Flex direction="row" gap={8}>
                      <Flex direction="column" gap={1}>
                        <Text variant="labelM" intent="secondary">
                          Amount Paid
                        </Text>
                        <Text variant="bodyM">{formatCurrency(displayAmount)}</Text>
                      </Flex>
                      <Flex direction="column" gap={1}>
                        <Text variant="labelM" intent="secondary">
                          Date Received
                        </Text>
                        <Text variant="bodyM">{manualDateValue}</Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </div>
              </Popover.Content>
            </Popover>
          </Flex>
        ) : (
          feeStatusValue
        ),
    },
    { label: 'Amount Paid', value: formatCurrency(displayAmount) },
  ];
  let split = items.length;
  if (props.lineSplit) {
    split = 3;
  }
  return (
    <Card emphasis="highlighted">
      <Flex direction="column">
        <Flex gap={3} align="center">
          <Heading level={2}>{program.name}</Heading>
          <Tag intent="secondary" size="md">
            {intakeTerm.name}
          </Tag>
          {application.meta.deferredProgramIntakeTerm && (
            <Text
              intent="secondary"
              contrast="mid"
              variant="labelS"
            >{`Deferred from ${application.meta.deferredProgramIntakeTerm.name}`}</Text>
          )}
        </Flex>
        <Divider direction="horizontal" />
        <AttributesView items={items.slice(0, split)} line="1" />
        {props.lineSplit && <AttributesView items={items.slice(split, items.length)} line="2" />}
      </Flex>
    </Card>
  );
}
